import { useState, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import { FormData, Section } from '../types';
import { FormSection } from './FormSection';
import { ProgressIndicator } from './ProgressIndicator';
import { cleanupHiddenAnswers, evaluateVisibility } from '../questionnaire/conditionEvaluator';

interface QuestionnaireProps {
  sections: Section[];
  formData: FormData;
  setFormData: Dispatch<SetStateAction<FormData>>;
  onSubmit: (data: FormData) => Promise<void> | void;
}

export const Questionnaire = ({
  sections,
  formData,
  setFormData,
  onSubmit
}: QuestionnaireProps) => {
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const currentSection = sections[currentSectionIndex];

  useEffect(() => {
    if (sectionRefs.current[currentSectionIndex]) {
      sectionRefs.current[currentSectionIndex]?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentSectionIndex]);

  const findNextNonEmptySection = (startIndex: number): number => {
    for (let i = startIndex + 1; i < sections.length; i++) {
      const section = sections[i];
      const visibleQuestions = section.questions.filter(q => evaluateVisibility(q.visibleWhen, formData));
      if (visibleQuestions.length > 0) {
        return i;
      }
    }
    return sections.length - 1;
  };

  const findPreviousNonEmptySection = (startIndex: number): number => {
    for (let i = startIndex - 1; i >= 0; i--) {
      const section = sections[i];
      const visibleQuestions = section.questions.filter(q => evaluateVisibility(q.visibleWhen, formData));
      if (visibleQuestions.length > 0) {
        return i;
      }
    }
    return 0;
  };

  const handleNext = () => {
    if (currentSectionIndex < sections.length - 1) {
      // Cleanup hidden answers before moving to next section
      const cleanedData = cleanupHiddenAnswers(sections, formData);
      setFormData(cleanedData);
      
      // Find next non-empty section
      const nextIndex = findNextNonEmptySection(currentSectionIndex);
      setCurrentSectionIndex(nextIndex);
    }
  };

  const handleBack = () => {
    if (currentSectionIndex > 0) {
      // Cleanup hidden answers before moving to previous section
      const cleanedData = cleanupHiddenAnswers(sections, formData);
      setFormData(cleanedData);
      
      // Find previous non-empty section
      const prevIndex = findPreviousNonEmptySection(currentSectionIndex);
      setCurrentSectionIndex(prevIndex);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Cleanup hidden answers before submission
      const cleanedData = cleanupHiddenAnswers(sections, formData);
      await onSubmit(cleanedData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLastSection = currentSectionIndex === sections.length - 1;

  return (
    <div className="questionnaire">
      <ProgressIndicator
        currentStep={currentSectionIndex + 1}
        totalSteps={sections.length}
      />

      <div className="form-sections">
        <div
          className="form-text"
          style={{
            marginBottom: '1rem',
            fontStyle: 'italic',
            color: '#666'
          }}
        >
          Note: Please do not enter sensitive personal information in free-text responses.
        </div>
        {sections.map((section, index) => (
          <div
            key={section.id}
            ref={el => { sectionRefs.current[index] = el; }}
            className={`form-section ${index === currentSectionIndex ? 'slide-in' : ''}`}
          >
            {index === currentSectionIndex && (
              <FormSection
                section={section}
                formData={formData}
                setFormData={setFormData}
                isLastSection={isLastSection}
                onNext={handleNext}
                onBack={handleBack}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};